<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import {
  NXGDSButton,
  NXGDSButtonVariants,
  NXGDSTagIndicator,
  NXGDSTagIndicatorSize,
  NXGDSTagIndicatorVariants,
} from '@nsftx/seven-nxg-design-system'
import { getShortcutKeys, useShortcutsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import { useWidgetsStore } from '@/stores/widgets/widgetsStore'
import { useCalculatorStore } from '@/stores/calculator/calculatorStore'
import SSShortcutKeys from '../../SSShortcutKeys.vue'
import SSCalculatorCurrencySelect from './SSCalculatorCurrencySelect.vue'
import SSCalculatorSummaryBar from './SSCalculatorSummaryBar.vue'
import SSCalculatorResetModal from './SSCalculatorResetModal.vue'
import { ROUTES_NAMES } from '@/router/routesNames'

const { getAppShortcutsByAction } = useShortcutsStore()

const { t } = useI18n({ useScope: 'global' })
const router = useRouter()
const { toggleCalculator } = useWidgetsStore()
const calculatorStore = useCalculatorStore()
const { openResetModal, toggleCompact } = calculatorStore
const { transactionCount, isCompact, currency } = storeToRefs(calculatorStore)

const resetSessionTitle = computed(() =>
  t('calculator_clear_session_currency', { currency: currency.value }),
)

const compactToggleTitle = computed(() =>
  isCompact.value ? t('calculator_full_screen') : t('calculator_pin_view'),
)

interface SSCalculatorWidgetProps {
  compact?: boolean
}

const { compact = true } = defineProps<SSCalculatorWidgetProps>()

const summaryLayout = computed(() => (compact ? 'multi-row' : 'inline'))

const calculatorClass = computed(() => ({
  'ss-calculator--minimized': isCompact.value,
  'ss-calculator--compact': compact,
  'ss-calculator--wide': !compact,
}))

const actionShortcut = computed(() => getAppShortcutsByAction('TOGGLE_CALCULATOR')?.shortcut[0])
const actionShortcutKeys = computed(() =>
  actionShortcut.value ? getShortcutKeys(actionShortcut.value) : [],
)

const navigateToTransactions = () => {
  router.push({ name: ROUTES_NAMES.calculatorTransactions })
}
</script>

<template>
  <div class="ss-calculator" :class="calculatorClass">
    <SSCalculatorResetModal />

    <div class="ss-calculator__container">
      <div class="ss-calculator__header">
        <div class="ss-calculator__header-left">
          <p class="ss-calculator__title">{{ t('widget_calculator') }}</p>
          <SSShortcutKeys :keys="actionShortcutKeys" />
        </div>

        <div class="ss-calculator__header-right">
          <NXGDSButton
            :variant="NXGDSButtonVariants.ghost"
            :icon="isCompact ? 'expand-a' : 'collapse-a'"
            size="sm"
            :title="compactToggleTitle"
            @click="toggleCompact"
          />
          <NXGDSButton
            v-if="transactionCount"
            :variant="NXGDSButtonVariants.ghost"
            icon="refresh"
            size="sm"
            :title="resetSessionTitle"
            @click="openResetModal"
          />
          <NXGDSButton
            :variant="NXGDSButtonVariants.ghost"
            icon="close-a"
            size="sm"
            @click="toggleCalculator"
          />
        </div>
      </div>

      <!-- Stacked: currency → metrics → result; transactions at panel bottom -->
      <div v-if="compact" class="ss-calculator__body ss-calculator__body--stacked">
        <SSCalculatorSummaryBar :layout="summaryLayout">
          <template #leading>
            <div class="ss-calculator__currency">
              <SSCalculatorCurrencySelect />
            </div>
          </template>
        </SSCalculatorSummaryBar>

        <div
          v-if="!isCompact"
          class="ss-calculator__transactions-trigger ss-calculator__transactions-trigger--row"
        >
          <NXGDSButton
            :variant="NXGDSButtonVariants.secondary"
            width="100%"
            @click="navigateToTransactions"
          >
            <div class="nxgds-btn__wrapper">
              <div class="nxgds-btn__icon">
                <i class="n-i n-i-betslip-b" />
              </div>
              <span class="transactions-btn__content">
                <span class="transactions-btn__label">{{
                  t('calculator_transactions_title')
                }}</span>
                <NXGDSTagIndicator
                  v-if="transactionCount > 0"
                  :label="String(transactionCount)"
                  :variant="NXGDSTagIndicatorVariants.subtle"
                  :size="NXGDSTagIndicatorSize.medium"
                />
              </span>
            </div>
          </NXGDSButton>
        </div>
      </div>

      <!-- Inline: single row for full-width horizontal calculator -->
      <div v-else class="ss-calculator__body ss-calculator__body--inline">
        <div class="ss-calculator__currency">
          <SSCalculatorCurrencySelect />
        </div>
        <SSCalculatorSummaryBar class="ss-calculator__summary" :layout="summaryLayout" />

        <div v-if="!isCompact && transactionCount" class="ss-calculator__transactions-trigger">
          <NXGDSButton
            :variant="NXGDSButtonVariants.secondary"
            width="100%"
            @click="navigateToTransactions"
          >
            <div class="nxgds-btn__wrapper">
              <div class="nxgds-btn__icon"><i class="n-i n-i-betslip-b"></i></div>
              <span class="transactions-btn__content">
                <NXGDSTagIndicator
                  v-if="transactionCount > 0"
                  :label="String(transactionCount)"
                  :variant="NXGDSTagIndicatorVariants.subtle"
                  :size="NXGDSTagIndicatorSize.medium"
                />
              </span>
            </div>
          </NXGDSButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@/styles/mixins' as mixins;

.ss-calculator {
  height: 100%;

  &__container {
    display: flex;
    flex-direction: column;
    gap: var(--nxgds-alias-spacing-2xs);
    height: 100%;
    min-height: 0;
    padding: var(--nxgds-alias-spacing-xs);
    border: 2px solid var(--nxgds-mapped-color-border-primary);
    border-radius: var(--nxgds-alias-radius-corner-md);
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--nxgds-alias-spacing-sm);
    padding: var(--nxgds-alias-spacing-xs) var(--nxgds-alias-spacing-2xs)
      var(--nxgds-alias-spacing-sm);
  }

  &__header-left,
  &__header-right {
    display: flex;
    align-items: center;
    gap: var(--nxgds-alias-spacing-xs);
    min-width: 0;
  }

  &__header-right {
    flex-shrink: 0;
  }

  &__title {
    color: var(--nxgds-mapped-color-text-secondary);
    @include mixins.typography('h4-medium');
    white-space: nowrap;
  }

  &__body {
    min-width: 0;
    padding: 0 var(--nxgds-alias-spacing-xs);

    &--stacked {
      display: flex;
      flex: 1 1 0;
      flex-direction: column;
      gap: var(--nxgds-alias-spacing-2xs);
      min-height: 0;
    }

    &--inline {
      display: flex;
      align-items: stretch;
      gap: var(--nxgds-alias-spacing-2xs);
    }
  }

  &__currency {
    flex: 0 0 auto;
    width: 6.5rem;
    min-width: 4.75rem;
  }

  &__summary {
    flex: 1 1 0;
    min-width: 0;
  }

  &__fill {
    flex-direction: column;
    margin: 0 var(--nxgds-alias-spacing-xs);
  }

  &__transactions-trigger {
    position: relative;
    flex: 0 0 auto;

    &--row {
      width: 100%;
    }
  }

  &__body--inline &__transactions-trigger {
    align-self: stretch;
  }

  &__transactions-button {
    min-height: 2.5rem;
  }

  &__body--inline &__transactions-button {
    height: 100%;
  }

  &__transactions-label {
    white-space: nowrap;
  }

  &__transactions-badge {
    position: absolute;
    top: -0.375rem;
    right: -0.375rem;
    pointer-events: none;
  }

  &__empty {
    flex: 1;
    min-height: 0;
    padding-inline: var(--nxgds-alias-spacing-md);
    padding-block: var(--nxgds-alias-spacing-xs);
    background-color: var(--nxgds-mapped-color-surface-component-subsection);
    color: var(--nxgds-mapped-color-text-secondary);
    @include mixins.typography('paragraph-regular');
    border-radius: var(--nxgds-alias-radius-corner-theme);
  }

  &--minimized {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    .ss-calculator__container {
      height: auto;
      flex: 0 0 auto;
    }

    .ss-calculator__fill,
    .ss-calculator__empty {
      display: none;
    }
  }

  .transactions-btn__content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--nxgds-alias-spacing-xs);
  }
  .nxgds-btn__wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--nxgds-alias-spacing-xs);
  }
}
</style>
